import { useEffect, useState } from "react";
import { Bell, X, DeviceMobile, Share, Plus, Download } from "@phosphor-icons/react";
import { PUSH_SUPPORTED } from "@/lib/push";
import usePwaInstall from "@/lib/usePwaInstall";
import { useT } from "@/lib/i18n";

/**
 * PwaInstallBanner — iOS "Add to Home Screen" hints + Android/Chrome
 * native install prompt + optional push-permission nudge.
 *
 * iter119 overhaul:
 *   - Listens to `beforeinstallprompt` so Chrome/Edge can show a real
 *     "Install app" button that triggers the browser's install flow
 *     (previously only iOS Safari was handled).
 *   - All strings localized via `useT()` so EN / FR / ES all render.
 *   - Dismissal is sticky for 14 days (localStorage).
 *   - Hides itself after `appinstalled` event.
 */
const STORAGE_KEY = "usair_pwa_banner_dismissed_until";

function isiOSLegacy() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function dismissedRecently() {
  try {
    const until = Number(localStorage.getItem(STORAGE_KEY) || 0);
    return until > Date.now();
  } catch (_) { return false; }
}

export default function PwaInstallBanner() {
  const t = useT();
  // Shared install lifecycle (also used by InstallAppButton on the
  // login page so the singleton beforeinstallprompt event isn't
  // captured twice).
  const { canInstall, isStandalone, install } = usePwaInstall();
  // variant: 'ios' | 'install' | 'push' | null
  const [variant, setVariant] = useState(null);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [installing, setInstalling] = useState(false);

  // Variant selection — install > ios > push, with cooldown + already-
  // installed guards.
  useEffect(() => {
    if (dismissedRecently() || isStandalone) { setVariant(null); return; }
    if (canInstall) { setVariant("install"); return; }
    if (isiOSLegacy() && !PUSH_SUPPORTED) { setVariant("ios"); return; }
    if (PUSH_SUPPORTED && Notification.permission === "default") {
      const timer = setTimeout(() => setVariant("push"), 5000);
      return () => clearTimeout(timer);
    }
    setVariant(null);
  }, [canInstall, isStandalone]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now() + 14 * 24 * 3600 * 1000)); } catch (_) { /* storage full */ }
    setVariant(null);
  };

  const requestPerm = async () => {
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p !== "default") setVariant(null);
    } catch (_) { /* ignore */ }
  };

  // Fire the stashed install prompt via the shared hook. The hook
  // clears the singleton automatically once userChoice resolves, and
  // the variant useEffect picks up the resulting `canInstall=false`
  // and hides the banner.
  const runInstall = async () => {
    if (!canInstall) return;
    setInstalling(true);
    try {
      const outcome = await install();
      if (outcome !== "accepted") {
        // User dismissed the native prompt — don't re-nag for 14 days.
        dismiss();
      }
    } finally {
      setInstalling(false);
    }
  };

  if (!variant) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] bg-slate-900 text-white rounded-sm shadow-2xl border border-slate-700 p-4 flex items-start gap-3"
    >
      {variant === "install" ? (
        <Download size={22} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
      ) : variant === "ios" ? (
        <DeviceMobile size={22} weight="fill" className="text-blue-400 shrink-0 mt-0.5" />
      ) : (
        <Bell size={22} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        {variant === "install" ? (
          <>
            <div className="font-bold text-sm mb-1">
              {t("pwa.install_title", "Install this app")}
            </div>
            <p className="text-xs text-slate-300 mb-3">
              {t("pwa.install_body", "Add US Air to your home screen for a native-app feel — no browser chrome, instant launch, and push notifications stay on.")}
            </p>
            <div className="flex gap-2">
              <button
                data-testid="pwa-banner-install"
                onClick={runInstall}
                disabled={installing}
                className="px-3 py-1.5 bg-emerald-500 text-black text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-emerald-400 disabled:opacity-40"
              >
                {installing
                  ? t("pwa.installing", "Installing…")
                  : t("pwa.install_cta", "Install")}
              </button>
              <button
                data-testid="pwa-banner-dismiss"
                onClick={dismiss}
                className="px-3 py-1.5 border border-slate-600 text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-slate-800"
              >
                {t("pwa.not_now", "Not now")}
              </button>
            </div>
          </>
        ) : variant === "ios" ? (
          <>
            <div className="font-bold text-sm mb-1">
              {t("pwa.ios_title", "Install on your iPhone")}
            </div>
            <p className="text-xs text-slate-300 mb-2">
              {t("pwa.ios_body", "For a native-app feel on iOS, add this to your home screen.")} {t("pwa.ios_tap", "Tap")}
              <Share size={11} weight="bold" className="inline mx-0.5" /> {t("pwa.ios_share", "Share")}, {t("pwa.ios_then", "then")}
              <Plus size={11} weight="bold" className="inline mx-0.5" /> {t("pwa.ios_add", "\"Add to Home Screen\".")}
            </p>
            <button
              data-testid="pwa-banner-dismiss"
              onClick={dismiss}
              className="text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-white"
            >
              {t("pwa.maybe_later", "Maybe later")}
            </button>
          </>
        ) : (
          <>
            <div className="font-bold text-sm mb-1">
              {t("pwa.push_title", "Get push notifications")}
            </div>
            <p className="text-xs text-slate-300 mb-3">
              {t("pwa.push_body", "Pop-up alerts for hot leads and incoming warranty claims — even when this tab is closed.")}
            </p>
            <div className="flex gap-2">
              <button
                data-testid="pwa-banner-allow"
                onClick={requestPerm}
                disabled={perm === "denied"}
                className="px-3 py-1.5 bg-amber-500 text-black text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-amber-400 disabled:opacity-40"
              >
                {t("pwa.allow", "Allow")}
              </button>
              <button
                data-testid="pwa-banner-dismiss"
                onClick={dismiss}
                className="px-3 py-1.5 border border-slate-600 text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-slate-800"
              >
                {t("pwa.not_now", "Not now")}
              </button>
            </div>
          </>
        )}
      </div>
      <button onClick={dismiss} className="text-slate-400 hover:text-white shrink-0" data-testid="pwa-banner-close">
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
